import type { BillingInterval, BillingPlan } from 'definitions/billing-plans';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';

export type BillingState = {
  billingMode: 'card' | 'free' | 'invoice';
  /** The account this payload describes: the caller's own company, or one of an agency's clients. */
  accountCompanyId: number;
  /** Set when an agency pays for this account's plan. */
  billedBy: { companyId: number; name: string } | null;
  /** Whether the caller is the one who buys, changes and cancels this account's plan. A client's own users are not. */
  isPayer: boolean;
  /** Whether there is already a plan to manage: the page offers the portal rather than a checkout the server would refuse. */
  hasLiveSubscription: boolean;
  subscriptionStatus: string | null;
  /** The plan being paid for, null without a subscription. */
  plan: BillingPlan | null;
  interval: BillingInterval | null;
  /** The plan is the one an agency's fee includes at no charge: there is nothing to buy or change. */
  planIncluded: boolean;
  /** A downgrade Polar holds until the period ends. Null when nothing is scheduled. */
  pendingChange: { plan: BillingPlan; interval: BillingInterval; appliesAt: string } | null;
  trialEnd: string | null;
  /** When the current period ends: the date a downgrade chosen now would apply. */
  currentPeriodEnd: string | null;
  /** When serving stops if a failed payment is not fixed. Null unless past due. */
  graceEndsAt: string | null;
  /**
   * Whether this company's experiments and surveys actually serve. Computed
   * server-side by the same function the generation-time gate uses, so the
   * banner cannot contradict enforcement. Do not re-derive it from
   * `subscriptionStatus`, which does not know about the free/invoice or
   * internal-traffic exemptions.
   */
  billingActive: boolean;
  currency: string;
  /**
   * The agency fee, for an agency account: not a plan, with no sessions or credits. Null for any other account.
   */
  agencyFee: {
    status: string | null;
    interval: BillingInterval | null;
    live: boolean;
    trialEnd: string | null;
    currentPeriodEnd: string | null;
  } | null;
  /** The account's own AI credit ledger; null for accounts that are not on credits (invoice, free, an agency itself). */
  aiCredits: {
    /** Spendable now: unexpired included credits plus unexpired packs. */
    balance: number;
    includedRemaining: number;
    includedGranted: number;
    /** When the included credits reset. */
    includedExpiresAt: string | null;
    packRemaining: number;
    nextPackExpiresAt: string | null;
  } | null;
  /** What AI credits were spent on in the last 30 days, per feature, biggest first. */
  aiSpend: { feature: string; credits: number }[];
  /** False when credit packs cannot be sold on this deployment (Polar not set up). */
  creditPacksAvailable: boolean;
  /** The free trial comes with a first subscription only; false once any subscription has existed. */
  trialAvailable: boolean;
  /** The same for the agency fee, which has its own trial. */
  agencyFeeTrialAvailable: boolean;
  /** Whether a plan checkout can start on this deployment (Polar set up). False shows "not available yet". */
  checkoutAvailable: boolean;
};

export const BILLING_STATE_KEY = ['BILLING_STATE'] as const;

type UseBillingStateParams = {
  /** An agency's client to describe; the caller's own account when omitted. */
  accountId?: number | null;
  /** Poll while a checkout's webhook is expected (see useCheckoutReturn). False otherwise. */
  refetchInterval?: number | false;
};

/**
 * The one hook every gate and banner reads. Backed by `GET /api/billing/state`:
 * subscription status and trial dates from our own webhook-written mirror (the
 * source enforcement reads), plus the AI credit balance from our own ledger
 * (see src/servers/api/billing/state.ts). One account at a time: the caller's own, or
 * an agency's client.
 */
export const useBillingState = ({ accountId = null, refetchInterval = false }: UseBillingStateParams = {}) => {
  const apiClient = useClient();
  const { user } = useAuth();

  const {
    data: billingState,
    isLoading,
    isError,
    refetch,
  } = useQuery<BillingState>({
    // Keyed by account under the shared prefix, so one invalidation refreshes every account's view.
    queryKey: [...BILLING_STATE_KEY, accountId ?? 'self'],
    enabled: !!user,
    queryFn: () => apiClient.get(accountId ? `/billing/state?accountCompanyId=${accountId}` : '/billing/state'),
    refetchInterval,
  });

  return { billingState, isLoading, isError, refetch };
};
